import * as React from 'react'
import { AlertCircle as WarningCircleIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  collectAdditionalProperties,
  useGetPlantPlcControllerTagsQuery,
  useGetPlantPlcNodeDetailQuery,
  usePlantSessionPaint,
  type PlantAOI,
  type PlantController,
  type PlantControlPattern,
  type PlantPlcControllerDetail,
  type PlantPlcAoiDetail,
  type PlantPlcDataTypeDetail,
  type PlantPlcDataTypeMemberDetail,
  type PlantPlcNodeKind,
  type PlantPlcProgramDetail,
  type PlantPlcRobotSignalMapping,
  type PlantPlcRoutineDetail,
  type PlantPlcScadaTag,
  type PlantPlcTagDerivation,
  type PlantPlcTagDetail,
  type PlantPlcDecoratedMember,
  type PlantPLCDataType,
  type PlantPLCTag,
  type PlantRoutine
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { cn } from '../../lib/utils'
import { plantHref } from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { plcIconFor, plcLabelFor } from './plc-icon'
import { PlcRepoLinkSection } from './PlcRepoLinkSection'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

/**
 * Right-pane detail body for the PLC tab. The panel owns the per-node
 * detail query and dispatches on the discriminated `kind` returned by
 * the server-side `getPlantPlcNodeDetail` endpoint. Each sub-view
 * reuses the same header / KPI / section vocabulary the other Plant
 * tabs use (`SectionHeader`, `Stat`, `EntityDetailsInspector`) so
 * the visual language stays consistent.
 */
export interface PlcDetailPanelProps {
  nodeId: string
}

const CONTROLLER_TAG_PAGE_SIZE = 50

export function PlcDetailPanel({ nodeId }: PlcDetailPanelProps): React.ReactElement {
  const detail = useGetPlantPlcNodeDetailQuery(nodeId, { skip: !nodeId })

  if (detail.isError) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <Empty
          title="Couldn't load this PLC node"
          hint="The node may have been deleted, or the proxy is unreachable."
          icon={WarningCircleIcon}
        />
      </div>
    )
  }
  if (detail.isLoading || !detail.data) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <div className="text-sm text-ink-3">Loading PLC node…</div>
      </div>
    )
  }

  switch (detail.data.kind) {
    case 'Controller':
      return <ControllerDetail key={detail.data.controller.id} detail={detail.data} />
    case 'Program':
      return <ProgramDetail key={detail.data.program.id} detail={detail.data} />
    case 'Routine':
      return <RoutineDetail detail={detail.data} />
    case 'AOI':
      return <AoiDetail detail={detail.data} />
    case 'PLCDataType':
      return <DataTypeDetail detail={detail.data} />
    case 'PLCDataTypeMember':
      return <DataTypeMemberDetail detail={detail.data} />
    case 'PLCTag':
      return <PlcTagDetail detail={detail.data} />
  }
}

function ControllerDetail({ detail }: { detail: PlantPlcControllerDetail }): React.ReactElement {
  const { controller, programs, aois, dataTypes, patterns, tagCount, mappedComputes } = detail
  const [tagPage, setTagPage] = React.useState(0)
  const controllerTags = useGetPlantPlcControllerTagsQuery({
    nodeId: controller.id,
    page: tagPage,
    pageSize: CONTROLLER_TAG_PAGE_SIZE
  })
  const programGhosts = useVisibleGhosts(controller.id, 'Program')
  const aoiGhosts = useVisibleGhosts(controller.id, 'AOI')
  const dataTypeGhosts = useVisibleGhosts(controller.id, 'PLCDataType')
  const tagGhosts = useVisibleGhosts(controller.id, 'PLCTag')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('Controller', controller as unknown as Record<string, unknown>),
    [controller]
  )
  return (
    <>
      <DetailHeader
        kind="Controller"
        entityId={controller.id}
        isStray={detail.isStray}
        name={controller.name}
        subtitle={controllerSubtitle(controller)}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Programs" value={programs.length} />
        <Stat label="AOIs" value={aois.length} />
        <Stat label="Data types" value={dataTypes.length} />
        <Stat label="Tags" value={tagCount} hint="all tags under this controller" />
        <Stat label="Patterns" value={patterns.length} />
      </div>
      <PlcRepoLinkSection controller={controller} />
      {mappedComputes.length > 0 && (
        <section>
          <SectionHeader
            label="Connected compute"
            subtitle={`${mappedComputes.length} ${mappedComputes.length === 1 ? 'host' : 'hosts'}`}
          />
          <div className="flex flex-wrap gap-2">
            {mappedComputes.map((compute) => (
              <Link
                key={compute.id}
                to={plantHref(compute.id, 'Compute', compute)}
                className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand-soft px-2.5 py-1 text-12 font-medium text-brand no-underline hover:bg-brand-soft/70"
              >
                {compute.compute_type && (
                  <span className="font-mono text-10 uppercase tracking-[0.04em] text-brand/80">
                    {compute.compute_type}
                  </span>
                )}
                <span>{compute.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {(programs.length > 0 || programGhosts.length > 0) && (
        <section>
          <SectionHeader
            label="Programs"
            subtitle={`${programs.length + programGhosts.length} ${
              programs.length + programGhosts.length === 1 ? 'program' : 'programs'
            }`}
          />
          <NodeTileGrid>
            {programs.map((program) => (
              <NodeTile
                key={program.id}
                entityId={program.id}
                href={plantHref(program.id, 'Program', program)}
                kind="Program"
                title={program.name}
                subtitle={program.type}
              />
            ))}
            {programGhosts.map((ghost) => (
              <PlcGhostTile key={ghost.id} kind="Program" ghost={ghost} />
            ))}
          </NodeTileGrid>
        </section>
      )}
      {(aois.length > 0 || aoiGhosts.length > 0) && (
        <section>
          <SectionHeader
            label="Add-On Instructions"
            subtitle={`${aois.length + aoiGhosts.length} ${
              aois.length + aoiGhosts.length === 1 ? 'AOI' : 'AOIs'
            }`}
          />
          <NodeTileGrid>
            {aois.map((aoi) => (
              <NodeTile
                key={aoi.id}
                entityId={aoi.id}
                href={plantHref(aoi.id, 'AOI', aoi)}
                kind="AOI"
                title={aoi.name}
                subtitle={aoi.revision ? `rev ${aoi.revision}` : aoi.description}
              />
            ))}
            {aoiGhosts.map((ghost) => (
              <PlcGhostTile key={ghost.id} kind="AOI" ghost={ghost} />
            ))}
          </NodeTileGrid>
        </section>
      )}
      {(dataTypes.length > 0 || dataTypeGhosts.length > 0) && (
        <section>
          <SectionHeader
            label="Data types"
            subtitle={`${dataTypes.length + dataTypeGhosts.length} ${
              dataTypes.length + dataTypeGhosts.length === 1 ? 'type' : 'types'
            }`}
          />
          <NodeTileGrid>
            {dataTypes.map((dataType) => (
              <NodeTile
                key={dataType.id}
                entityId={dataType.id}
                href={plantHref(dataType.id, 'PLCDataType', dataType)}
                kind="PLCDataType"
                title={dataType.name}
                subtitle={dataType.description}
              />
            ))}
            {dataTypeGhosts.map((ghost) => (
              <PlcGhostTile key={ghost.id} kind="PLCDataType" ghost={ghost} />
            ))}
          </NodeTileGrid>
        </section>
      )}
      {patterns.length > 0 && (
        <section>
          <SectionHeader label="Control Patterns" subtitle={`${patterns.length}`} />
          <div className="flex flex-wrap gap-2">
            {patterns.map((pattern) => (
              <ControlPatternChip key={pattern.id} pattern={pattern} />
            ))}
          </div>
        </section>
      )}
      {controllerTags.isFetching && !controllerTags.data && (
        <TagPageLoading label="controller tags" />
      )}
      {controllerTags.isError && <TagPageError label="controller tags" />}
      {controllerTags.data && (controllerTags.data.totalTags > 0 || tagGhosts.length > 0) && (
        <PlcTagTable
          tags={controllerTags.data.tags}
          sectionLabel="Controller tags"
          scadaByTagId={controllerTags.data.scadaByTagId}
          ghosts={tagGhosts}
          pagination={{
            page: tagPage,
            pageSize: CONTROLLER_TAG_PAGE_SIZE,
            total: controllerTags.data.totalTags,
            onPageChange: setTagPage
          }}
        />
      )}
      <EntityDetailsInspector entity={controller} additionalProperties={additionalProperties} />
    </>
  )
}

function controllerSubtitle(controller: PlantController): string | undefined {
  const parts: string[] = []
  if (controller.type) parts.push(controller.type)
  if (controller.vendor) parts.push(controller.vendor)
  if (controller.firmware_version) parts.push(`firmware ${controller.firmware_version}`)
  return parts.length > 0 ? parts.join(' · ') : undefined
}

function ProgramDetail({ detail }: { detail: PlantPlcProgramDetail }): React.ReactElement {
  const { program, controller, routines, tags, recursiveTagCount, scadaByTagId } = detail
  const [controllerTagPage, setControllerTagPage] = React.useState(0)
  const controllerTags = useGetPlantPlcControllerTagsQuery({
    nodeId: program.id,
    page: controllerTagPage,
    pageSize: CONTROLLER_TAG_PAGE_SIZE
  })
  const routinesWithSource = React.useMemo(
    () => routines.filter((routine) => routine.source_code),
    [routines]
  )
  const routineGhosts = useVisibleGhosts(program.id, 'Routine')
  const tagGhosts = useVisibleGhosts(program.id, 'PLCTag')
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('Program', program as unknown as Record<string, unknown>),
    [program]
  )
  return (
    <>
      <DetailHeader
        kind="Program"
        entityId={program.id}
        isStray={detail.isStray}
        name={program.name}
        parent={controller ? { kind: 'Controller', node: controller } : null}
        subtitle={program.type}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Routines" value={routines.length} />
        <Stat label="Tags" value={recursiveTagCount} hint="program + routines" />
        <Stat label="Direct tags" value={tags.length} hint="owned by this program only" />
        <Stat
          label="Referenced controller tags"
          value={controllerTags.data?.totalTags ?? '—'}
          hint="read or written by this program's routines"
        />
      </div>
      {(routines.length > 0 || routineGhosts.length > 0) && (
        <section>
          <SectionHeader label="Routines" subtitle={`${routines.length + routineGhosts.length}`} />
          <NodeTileGrid>
            {routines.map((routine) => (
              <NodeTile
                key={routine.id}
                entityId={routine.id}
                href={plantHref(routine.id, 'Routine', routine)}
                kind="Routine"
                title={routine.name}
                subtitle={routine.type}
              />
            ))}
            {routineGhosts.map((ghost) => (
              <PlcGhostTile key={ghost.id} kind="Routine" ghost={ghost} />
            ))}
          </NodeTileGrid>
        </section>
      )}
      {(tags.length > 0 || tagGhosts.length > 0) && (
        <PlcTagTable
          tags={tags}
          sectionLabel="Direct tags"
          scadaByTagId={scadaByTagId}
          ghosts={tagGhosts}
        />
      )}
      {controllerTags.isFetching && !controllerTags.data && (
        <TagPageLoading label="referenced controller tags" />
      )}
      {controllerTags.isError && <TagPageError label="referenced controller tags" />}
      {controllerTags.data && controllerTags.data.totalTags > 0 && (
        <PlcTagTable
          tags={controllerTags.data.tags}
          sectionLabel="Referenced controller tags"
          scadaByTagId={controllerTags.data.scadaByTagId}
          pagination={{
            page: controllerTagPage,
            pageSize: CONTROLLER_TAG_PAGE_SIZE,
            total: controllerTags.data.totalTags,
            onPageChange: setControllerTagPage
          }}
        />
      )}
      {routinesWithSource.length > 0 && <ProgramSourceSection routines={routinesWithSource} />}
      <EntityDetailsInspector entity={program} additionalProperties={additionalProperties} />
    </>
  )
}

function RoutineDetail({ detail }: { detail: PlantPlcRoutineDetail }): React.ReactElement {
  const { routine, program, controller, tags, scadaByTagId, readsTags, writesTags } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('Routine', routine as unknown as Record<string, unknown>, [
        'source_code',
        'rungs'
      ]),
    [routine]
  )
  return (
    <>
      <DetailHeader
        kind="Routine"
        entityId={routine.id}
        isStray={detail.isStray}
        name={routine.name}
        parent={program ? { kind: 'Program', node: program } : null}
        secondaryParent={controller ? { kind: 'Controller', node: controller } : null}
        subtitle={routine.type}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Tags" value={tags.length} />
        <Stat label="Rungs" value={routine.rungs?.length ?? 0} />
      </div>
      {tags.length > 0 && (
        <PlcTagTable tags={tags} sectionLabel="Tags" scadaByTagId={scadaByTagId} />
      )}
      <PlcTagLinkList label="Reads" tags={readsTags} />
      <PlcTagLinkList label="Writes" tags={writesTags} />
      {routine.source_code && <SourceCodeSection code={routine.source_code} />}
      <EntityDetailsInspector entity={routine} additionalProperties={additionalProperties} />
    </>
  )
}

function AoiDetail({ detail }: { detail: PlantPlcAoiDetail }): React.ReactElement {
  const { aoi, controller, tags, instantiatedTagCount, scadaByTagId } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('AOI', aoi as unknown as Record<string, unknown>, [
        'parameters',
        'source_code'
      ]),
    [aoi]
  )
  const parameters = aoi.parameters ?? []
  return (
    <>
      <DetailHeader
        kind="AOI"
        entityId={aoi.id}
        isStray={detail.isStray}
        name={aoi.name}
        parent={controller ? { kind: 'Controller', node: controller } : null}
        subtitle={aoi.revision ? `rev ${aoi.revision}` : aoi.description}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Parameters" value={parameters.length} />
        <Stat label="Tags" value={tags.length} />
        <Stat label="Instances" value={instantiatedTagCount} />
      </div>
      {aoi.description && <p className="m-0 max-w-3xl text-sm text-ink-3">{aoi.description}</p>}
      {parameters.length > 0 && <AoiParametersSection parameters={parameters} />}
      {tags.length > 0 && (
        <PlcTagTable tags={tags} sectionLabel="Tags" scadaByTagId={scadaByTagId} />
      )}
      {aoi.source_code && <SourceCodeSection code={aoi.source_code} />}
      <EntityDetailsInspector entity={aoi} additionalProperties={additionalProperties} />
    </>
  )
}

function DataTypeDetail({ detail }: { detail: PlantPlcDataTypeDetail }): React.ReactElement {
  const { dataType, controller, members, mappedUdt, tags } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('PLCDataType', dataType as unknown as Record<string, unknown>),
    [dataType]
  )
  return (
    <>
      <DetailHeader
        kind="PLCDataType"
        entityId={dataType.id}
        isStray={detail.isStray}
        name={dataType.name}
        parent={controller ? { kind: 'Controller', node: controller } : null}
        subtitle={dataType.description}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Members" value={members.length} />
        <Stat label="Used by tags" value={tags.length} />
        <Stat label="Mapped Ignition UDT" value={mappedUdt ? 'yes' : 'no'} />
      </div>
      {dataType.description && (
        <p className="m-0 max-w-3xl text-sm text-ink-3">{dataType.description}</p>
      )}
      {mappedUdt && (
        <section>
          <SectionHeader label="Mapped Ignition UDT" />
          <div className="flex flex-wrap gap-2">
            <Link
              to={plantHref(mappedUdt.id, 'UDTDefinition')}
              className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand-soft px-2.5 py-1 text-12 font-medium text-brand no-underline hover:bg-brand-soft/70"
            >
              <span>{mappedUdt.name}</span>
            </Link>
          </div>
        </section>
      )}
      <PlcTagLinkList label="Used by" tags={tags} />
      {members.length > 0 && (
        <section>
          <SectionHeader
            label="Members"
            subtitle={`${members.length} ${members.length === 1 ? 'member' : 'members'}`}
          />
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[28rem] border-collapse text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-[0.04em] text-ink-3">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Data type</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id} className="border-t">
                    <td className="px-3 py-2">
                      <Link
                        to={plantHref(member.id, 'PLCDataTypeMember', member)}
                        className="font-medium text-brand no-underline hover:underline"
                      >
                        {member.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-2">
                      {member.data_type ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-3">{member.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <EntityDetailsInspector entity={dataType} additionalProperties={additionalProperties} />
    </>
  )
}

function DataTypeMemberDetail({
  detail
}: {
  detail: PlantPlcDataTypeMemberDetail
}): React.ReactElement {
  const { member, dataType, controller } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties(
        'PLCDataTypeMember',
        member as unknown as Record<string, unknown>
      ),
    [member]
  )
  return (
    <>
      <DetailHeader
        kind="PLCDataTypeMember"
        entityId={member.id}
        isStray={detail.isStray}
        name={member.name}
        parent={dataType ? { kind: 'PLCDataType', node: dataType } : null}
        secondaryParent={controller ? { kind: 'Controller', node: controller } : null}
        subtitle={member.data_type}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Data type" value={member.data_type ?? '—'} />
      </div>
      {member.description && (
        <p className="m-0 max-w-3xl text-sm text-ink-3">{member.description}</p>
      )}
      <EntityDetailsInspector entity={member} additionalProperties={additionalProperties} />
    </>
  )
}

function PlcTagDetail({ detail }: { detail: PlantPlcTagDetail }): React.ReactElement {
  const {
    tag,
    dataType,
    owner,
    scadaTags,
    influences,
    readByRoutines,
    writtenByRoutines,
    calculatedFrom,
    feeds,
    robotSignals
  } = detail
  const decoratedMembers = React.useMemo(() => extractDecoratedMembers(tag), [tag])
  const decoratedMemberRows = React.useMemo(
    () => buildDecoratedMemberRows(tag, decoratedMembers, scadaTags),
    [tag, decoratedMembers, scadaTags]
  )
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('PLCTag', tag as unknown as Record<string, unknown>, [
        'decorated_data_type',
        'decorated_members'
      ]),
    [tag]
  )
  return (
    <>
      <DetailHeader
        kind="PLCTag"
        entityId={tag.id}
        isStray={detail.isStray}
        name={tag.name}
        parent={owner ? { kind: owner.kind, node: { id: owner.id, name: owner.name } } : null}
        subtitle={tagSubtitle(tag, dataType)}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Direction" value={tag.direction ?? '—'} />
        <Stat label="Array" value={tag.is_array ? 'yes' : 'no'} />
        <Stat label="Default" value={tag.default_value ?? '—'} />
      </div>
      {tag.description && <p className="m-0 max-w-3xl text-sm text-ink-3">{tag.description}</p>}
      {decoratedMemberRows.length > 0 && (
        <DecoratedMembersSection dataType={tag.decorated_data_type} rows={decoratedMemberRows} />
      )}
      {scadaTags.length > 0 && decoratedMemberRows.length === 0 && (
        <ScadaTagsSection tags={scadaTags} />
      )}
      <RobotSignalsSection mappings={robotSignals} />
      <RoutineLinkList label="Read by" routines={readByRoutines} />
      <RoutineLinkList label="Written by" routines={writtenByRoutines} />
      <DerivationSection label="Calculated from" rows={calculatedFrom} />
      <DerivationSection label="Feeds" rows={feeds} />
      {influences.length > 0 && <InfluencesSection influences={influences} />}
      <EntityDetailsInspector entity={tag} additionalProperties={additionalProperties} />
    </>
  )
}

function extractDecoratedMembers(tag: PlantPLCTag): PlantPlcDecoratedMember[] {
  const raw = tag.decorated_members
  if (!raw) return []
  if (Array.isArray(raw)) return raw.filter(isDecoratedMember)
  if (typeof raw !== 'string') return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter(isDecoratedMember) : []
  } catch {
    return []
  }
}

function isDecoratedMember(value: unknown): value is PlantPlcDecoratedMember {
  return Boolean(
    value &&
    typeof value === 'object' &&
    typeof (value as Partial<PlantPlcDecoratedMember>).name === 'string'
  )
}

function decoratedMemberDataType(member: PlantPlcDecoratedMember): string | undefined {
  return member.dataType ?? member.data_type
}

interface DecoratedMemberRow {
  member: PlantPlcDecoratedMember
  scadaTag?: PlantPlcScadaTag
}

function buildDecoratedMemberRows(
  tag: PlantPLCTag,
  members: PlantPlcDecoratedMember[],
  scadaTags: PlantPlcScadaTag[]
): DecoratedMemberRow[] {
  return members.map((member) => {
    const scadaTag = scadaTags.find(
      (candidate) => decoratedMemberNameForScadaTag(candidate, tag.name) === member.name
    )
    return {
      member,
      scadaTag
    }
  })
}

function decoratedMemberNameForScadaTag(
  scadaTag: PlantPlcScadaTag,
  plcTagName: string
): string | undefined {
  if (scadaTag.plc_tag_path) {
    const escaped = escapeRegExp(plcTagName)
    const match = scadaTag.plc_tag_path.match(new RegExp(`(?:^|\\.)${escaped}\\.([^.[\\]]+)`))
    if (match?.[1]) return match[1]
  }
  return lastPathSegment(scadaTag.tag_path ?? scadaTag.name)
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function lastPathSegment(value: string): string {
  const withoutProvider = value.replace(/^\[[^\]]+\]/, '')
  const normalized = withoutProvider.replace(/[.[\]]/g, '/')
  const parts = normalized.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? value
}

function tagSubtitle(
  tag: PlantPLCTag,
  dataType: PlantPLCDataType | null
): React.ReactNode | undefined {
  if (!tag.data_type) return undefined
  const typeHref = dataType ? plantHref(dataType.id, 'PLCDataType', dataType) : undefined
  return (
    <>
      {'Data Type: '}
      {typeHref ? (
        <Link to={typeHref} className="text-brand no-underline hover:underline">
          {tag.data_type}
        </Link>
      ) : (
        tag.data_type
      )}
    </>
  )
}

interface DetailHeaderProps {
  kind: PlantPlcNodeKind
  entityId: string
  isStray: boolean
  name: string
  parent?: { kind: PlantPlcNodeKind; node: { id: string; name: string } } | null
  secondaryParent?: { kind: PlantPlcNodeKind; node: { id: string; name: string } } | null
  subtitle?: React.ReactNode
}

function DetailHeader({
  kind,
  entityId,
  isStray,
  name,
  parent,
  secondaryParent,
  subtitle
}: DetailHeaderProps): React.ReactElement {
  const Icon = plcIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <header className={sessionDetailHeaderClass('flex flex-col gap-2', mark)}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        {parent ? (
          <Link
            to={plantHref(parent.node.id, parent.kind, parent.node)}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
          >
            <span aria-hidden>↑</span>
            <span>{parent.node.name}</span>
          </Link>
        ) : (
          <span className="text-11 uppercase tracking-[0.04em]">{plcLabelFor(kind)}</span>
        )}
        {secondaryParent && (
          <>
            <span aria-hidden>·</span>
            <Link
              to={plantHref(secondaryParent.node.id, secondaryParent.kind, secondaryParent.node)}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
            >
              <span>{secondaryParent.node.name}</span>
            </Link>
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-2xl font-semibold text-ink">{name}</h1>
            <SessionTitleMark mark={mark} />
            <PlantStrayPill isStray={isStray} />
          </div>
          {subtitle && <p className="m-0 mt-0.5 text-sm text-ink-3">{subtitle}</p>}
        </div>
      </div>
    </header>
  )
}

function PlcTagTable({
  tags,
  sectionLabel,
  scadaByTagId,
  ghosts = [],
  pagination
}: {
  tags: PlantPLCTag[]
  sectionLabel: string
  /**
   * Connected SCADA `Tag` nodes per PLCTag id, written by the PLC tag
   * → SCADA tag linker. The column is rendered for every table even
   * when the map is empty: an empty row signals "not yet linked" so
   * the operator can tell which PLCTags are still un-mapped at a
   * glance.
   */
  scadaByTagId: Record<string, PlantPlcScadaTag[]>
  ghosts?: PlantSessionGhostView[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
  }
}): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const total = (pagination?.total ?? tags.length) + ghosts.length
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1
  const start = pagination ? pagination.page * pagination.pageSize : 0
  const end = pagination ? Math.min(pagination.total, start + tags.length) : tags.length
  const showPagination = pagination !== undefined && pagination.total > pagination.pageSize
  return (
    <section>
      <SectionHeader
        label={sectionLabel}
        subtitle={`${total} ${total === 1 ? 'tag' : 'tags'}${
          showPagination ? ` · ${start + 1}–${end} shown` : ''
        }`}
      />
      <div className="overflow-x-auto rounded-xl border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Data type</th>
              <th className="px-4 py-2">Direction</th>
              <th className="px-4 py-2">SCADA tags</th>
              <th className="px-4 py-2">Description</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => {
              const scada = scadaByTagId[tag.id] ?? []
              const mark = markFor(tag.id)
              return (
                <tr
                  key={tag.id}
                  className="border-t border-border/40 transition-colors hover:bg-accent/30"
                >
                  <td className="px-4 py-2">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Link
                        to={plantHref(tag.id, 'PLCTag', tag)}
                        className="min-w-0 flex-1 font-medium text-ink no-underline hover:underline"
                      >
                        {tag.name}
                      </Link>
                      <SessionMarkAffordances mark={mark} />
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-12 text-ink-2">{tag.data_type ?? '—'}</td>
                  <td className="px-4 py-2 text-12 text-ink-2">{tag.direction ?? '—'}</td>
                  <td className="px-4 py-2">
                    <ScadaTagChips tags={scada} />
                  </td>
                  <td className="px-4 py-2 text-12 text-ink-3">{tag.description ?? ''}</td>
                </tr>
              )
            })}
            {ghosts.map((ghost) => (
              <tr
                key={ghost.id}
                className={
                  ghost.view === 'dimmed'
                    ? 'border-t border-line bg-bg-sunk/70'
                    : 'border-t border-st-down/30 bg-st-down-bg'
                }
              >
                <td className="px-4 py-2" colSpan={5}>
                  <PlcGhostTile kind="PLCTag" ghost={ghost} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && showPagination && (
        <div
          className="mt-2 flex items-center justify-end gap-2"
          aria-label={`${sectionLabel} pagination`}
        >
          <span className="mr-1 whitespace-nowrap text-11 text-ink-4">
            Page {pagination.page + 1} of {totalPages}
          </span>
          <TagPageButton
            disabled={pagination.page === 0}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
            label="Prev"
          />
          <TagPageButton
            disabled={pagination.page === totalPages - 1}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            label="Next"
          />
        </div>
      )}
    </section>
  )
}

function TagPageButton({
  disabled,
  onClick,
  label
}: {
  disabled: boolean
  onClick: () => void
  label: string
}): React.ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md border px-2.5 py-1 text-11 transition-colors',
        disabled
          ? 'cursor-not-allowed border-line text-ink-4'
          : 'cursor-pointer border-line text-ink-2 hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none'
      )}
    >
      {label}
    </button>
  )
}

function TagPageLoading({ label }: { label: string }): React.ReactElement {
  return <div className="text-sm text-ink-3">Loading {label}…</div>
}

function TagPageError({ label }: { label: string }): React.ReactElement {
  return <div className="text-sm text-st-down">Couldn't load {label}.</div>
}

/**
 * Inline chip column used inside `PlcTagTable`. Reuses the visual
 * vocabulary of `ScadaTagsSection` (brand-soft pill, edge token, optional
 * provider) but renders at table-row density (smaller padding/text)
 * and degrades to a dim "—" when no SCADA tag has been linked yet —
 * so the operator can quickly scan which PLCTags still need a link.
 */
function ScadaTagChips({ tags }: { tags: PlantPlcScadaTag[] }): React.ReactElement {
  if (tags.length === 0) {
    return <span className="text-12 text-ink-4">—</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((entry) => (
        <ScadaTagChip key={`${entry.edge}:${entry.id}`} entry={entry} size="sm" />
      ))}
    </div>
  )
}

function ScadaTagChip({
  entry,
  size
}: {
  entry: PlantPlcScadaTag
  size: 'sm' | 'md'
}): React.ReactElement {
  const props = entry.relationship?.props ?? {}
  const method =
    typeof props['mapping_method'] === 'string'
      ? props['mapping_method']
      : typeof props['method'] === 'string'
        ? props['method']
        : undefined
  const needsReview = props['needs_review'] === true
  const confidence = entry.relationship?.confidence
  const titleParts = [
    entry.name,
    'maps_to_tag',
    method ? `method: ${method}` : null,
    confidence !== undefined ? `confidence: ${Math.round(confidence * 100)}%` : null,
    needsReview ? 'needs review' : null
  ].filter(Boolean)
  const pad = size === 'sm' ? 'px-2 py-0.5 text-11' : 'px-2.5 py-1 text-12'
  const tokenSize = size === 'sm' ? 'text-9' : 'text-10'
  const nameMax = size === 'sm' ? 'max-w-[10rem]' : 'max-w-[14rem]'
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entry.id)

  return (
    <Link
      to={scadaTagHref(entry)}
      className={sessionMarkClass(
        cn(
          'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border border-brand/40 bg-brand-soft font-medium text-brand no-underline hover:bg-brand-soft/70',
          pad
        ),
        mark
      )}
      title={titleParts.join(' · ')}
    >
      <span className={`shrink-0 font-mono uppercase tracking-[0.04em] text-brand/80 ${tokenSize}`}>
        maps
      </span>
      <span className={`min-w-0 truncate ${nameMax}`}>{entry.name}</span>
      <SessionMarkAffordances mark={mark} />
      {entry.provider_name && (
        <span className={`shrink-0 ${tokenSize} text-brand/80`}>[{entry.provider_name}]</span>
      )}
      {method && <span className={`shrink-0 font-mono ${tokenSize} text-brand/70`}>{method}</span>}
      {needsReview && <MappingReviewBadge />}
    </Link>
  )
}

function MappingReviewBadge(): React.ReactElement {
  return (
    <span
      aria-label="Needs review"
      title="Needs review"
      className="shrink-0 rounded border border-amber-500/50 bg-amber-500/10 px-1 py-px text-9 font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
    >
      Review
    </span>
  )
}

/**
 * Pick the canonical destination for a SCADA-tag chip.
 *
 * Prefers the Plant Viewer's compute-host tag explorer
 * (`/plant/compute/hosts/<host>?provider=<tp>&tag=<name>`) so the
 * user lands inside the tag browser already scoped to the right
 * provider with the row highlighted, instead of being kicked over
 * to the raw graph viewer. Falls back to a graph-highlight link
 * when the SCADA tag has no resolvable provider/host (legacy data).
 */
function scadaTagHref(entry: PlantPlcScadaTag): string {
  return plantHref(entry.id, 'Tag', entry)
}

function DecoratedMembersSection({
  dataType,
  rows
}: {
  dataType?: string
  rows: DecoratedMemberRow[]
}): React.ReactElement {
  return (
    <section>
      <div className="mb-2 flex flex-col gap-0.5">
        <div className="flex items-baseline gap-3">
          <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
            Decorated Members
          </h3>
          <span className="text-xs text-ink-3">
            {rows.length} {rows.length === 1 ? 'member' : 'members'}
          </span>
        </div>
        {dataType && <div className="text-xs text-ink-3">Structure data type: {dataType}</div>}
      </div>
      <div className="overflow-x-auto rounded-xl border bg-card text-card-foreground shadow-sm">
        <table className="w-auto min-w-135 table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-32.5" />
            <col className="w-55" />
            <col className="w-70" />
          </colgroup>
          <thead>
            <tr className="text-left text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-4 py-2">Data type</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2 font-semibold text-ink-2">SCADA tag</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ member, scadaTag }, idx) => (
              <tr key={`${member.name}:${idx}`} className="border-t border-border/40">
                <td className="px-4 py-2 font-mono text-12 text-ink-2">
                  {decoratedMemberDataType(member) ?? '—'}
                </td>
                <td className="truncate px-4 py-2 font-medium text-ink" title={member.name}>
                  {member.name}
                </td>
                <td className="px-4 py-2">
                  {scadaTag ? (
                    <ScadaTagChips tags={[scadaTag]} />
                  ) : (
                    <span className="text-12 text-ink-4">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ScadaTagsSection({ tags }: { tags: PlantPlcTagDetail['scadaTags'] }): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Maps to SCADA"
        subtitle={`${tags.length} ${tags.length === 1 ? 'tag' : 'tags'}`}
      />
      <div className="flex flex-wrap gap-2">
        {tags.map((entry) => (
          <ScadaTagChip key={`${entry.edge}:${entry.id}`} entry={entry} size="md" />
        ))}
      </div>
    </section>
  )
}

function PlcTagLinkList({
  label,
  tags
}: {
  label: string
  tags: PlantPLCTag[]
}): React.ReactElement | null {
  if (tags.length === 0) return null
  return (
    <section>
      <SectionHeader
        label={label}
        subtitle={`${tags.length} ${tags.length === 1 ? 'tag' : 'tags'}`}
      />
      <ul className="m-0 flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
        {tags.map((tag) => (
          <li key={tag.id} className="flex items-center gap-2 text-sm text-ink-2">
            <Link
              to={plantHref(tag.id, 'PLCTag', tag)}
              className="font-medium text-ink no-underline hover:underline"
            >
              {tag.name}
            </Link>
            {tag.data_type && <span className="font-mono text-11 text-ink-3">{tag.data_type}</span>}
          </li>
        ))}
      </ul>
    </section>
  )
}

function RoutineLinkList({
  label,
  routines
}: {
  label: string
  routines: PlantRoutine[]
}): React.ReactElement | null {
  if (routines.length === 0) return null
  return (
    <section>
      <SectionHeader
        label={label}
        subtitle={`${routines.length} ${routines.length === 1 ? 'routine' : 'routines'}`}
      />
      <ul className="m-0 flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
        {routines.map((routine) => (
          <li key={routine.id} className="flex items-center gap-2 text-sm text-ink-2">
            <Link
              to={plantHref(routine.id, 'Routine', routine)}
              className="font-medium text-ink no-underline hover:underline"
            >
              {routine.name}
            </Link>
            {routine.type && (
              <Badge variant="outline" className="text-10">
                {routine.type}
              </Badge>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function DerivationSection({
  label,
  rows
}: {
  label: string
  rows: PlantPlcTagDerivation[]
}): React.ReactElement | null {
  if (rows.length === 0) return null
  return (
    <section>
      <SectionHeader
        label={label}
        subtitle={`${rows.length} ${rows.length === 1 ? 'tag' : 'tags'}`}
      />
      <ul className="m-0 flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
        {rows.map((row, idx) => (
          <li
            key={`${row.tag.id}:${row.viaRoutineUrn ?? ''}:${row.viaRungNumber ?? ''}:${idx}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-2"
          >
            <Link
              to={plantHref(row.tag.id, 'PLCTag', row.tag)}
              className="font-medium text-ink no-underline hover:underline"
            >
              {row.tag.name}
            </Link>
            {row.viaRoutineUrn && (
              <Link
                to={plantHref(row.viaRoutineUrn, 'Routine')}
                className="font-mono text-11 text-ink-3 no-underline hover:underline"
                title={row.viaRoutineUrn}
              >
                via {shortUrn(row.viaRoutineUrn)}
              </Link>
            )}
            {typeof row.viaRungNumber === 'number' && (
              <span className="font-mono text-11 text-ink-4">rung {row.viaRungNumber}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function RobotSignalsSection({
  mappings
}: {
  mappings: PlantPlcRobotSignalMapping[]
}): React.ReactElement | null {
  if (mappings.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Robot signals"
        subtitle={`${mappings.length} ${mappings.length === 1 ? 'mapping' : 'mappings'}`}
      />
      <div className="flex flex-wrap gap-2">
        {mappings.map((mapping) => (
          <Link
            key={mapping.signal.id}
            to={plantHref(mapping.signal.id, 'RobotIOSignal', mapping.signal)}
            className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-12 font-medium text-amber-800 no-underline hover:bg-amber-500/20 dark:text-amber-200"
            title={[
              mapping.signal.name,
              mapping.method ? `method: ${mapping.method}` : null,
              mapping.register ? `register: ${mapping.register}` : null,
              mapping.direction ? `direction: ${mapping.direction}` : null,
              mapping.confidence !== undefined
                ? `confidence: ${Math.round(mapping.confidence * 100)}%`
                : null
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <span className="min-w-0 max-w-[14rem] truncate">{mapping.signal.name}</span>
            {mapping.method && (
              <span className="shrink-0 font-mono text-10 text-amber-700/80 dark:text-amber-200/80">
                {mapping.method}
              </span>
            )}
            {mapping.register && (
              <span className="shrink-0 font-mono text-10 text-amber-700/80 dark:text-amber-200/80">
                {mapping.register}
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  )
}

function shortUrn(urn: string): string {
  const idx = urn.lastIndexOf(':')
  return idx >= 0 ? urn.slice(idx + 1) : urn
}

const INFLUENCE_LABEL: Record<PlantPlcTagDetail['influences'][number]['edge'], string> = {
  TRIGGERS: 'Triggers',
  GATES: 'Gates',
  INHIBITS: 'Inhibits',
  CONTROLS: 'Controls'
}

function InfluencesSection({
  influences
}: {
  influences: PlantPlcTagDetail['influences']
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Influences"
        subtitle={`${influences.length} ${influences.length === 1 ? 'edge' : 'edges'}`}
      />
      <ul className="m-0 flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
        {influences.map((entry, idx) => (
          <li
            key={`${entry.edge}:${entry.target.id}:${entry.outgoing}:${idx}`}
            className="flex items-center gap-2 text-sm text-ink-2"
          >
            <Badge variant="outline" className="font-mono text-10 uppercase tracking-[0.04em]">
              {INFLUENCE_LABEL[entry.edge]}
            </Badge>
            <span aria-hidden className="text-ink-3">
              {entry.outgoing ? '→' : '←'}
            </span>
            <Link
              to={plantHref(entry.target.id, 'PLCTag', entry.target)}
              className="font-medium text-ink no-underline hover:underline"
            >
              {entry.target.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function AoiParametersSection({
  parameters
}: {
  parameters: NonNullable<PlantAOI['parameters']>
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Parameters" subtitle={`${parameters.length}`} />
      <div className="overflow-x-auto rounded-xl border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Direction</th>
              <th className="px-4 py-2">Description</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((param, idx) => {
              const obj = param as Record<string, unknown>
              const name = typeof obj['name'] === 'string' ? obj['name'] : `#${idx + 1}`
              const dataType =
                typeof obj['data_type'] === 'string'
                  ? obj['data_type']
                  : typeof obj['type'] === 'string'
                    ? obj['type']
                    : '—'
              const direction = typeof obj['direction'] === 'string' ? obj['direction'] : '—'
              const description = typeof obj['description'] === 'string' ? obj['description'] : ''
              return (
                <tr key={`${name}:${idx}`} className="border-t border-border/40">
                  <td className="px-4 py-2 font-medium text-ink">{name}</td>
                  <td className="px-4 py-2 font-mono text-12 text-ink-2">{dataType}</td>
                  <td className="px-4 py-2 text-12 text-ink-2">{direction}</td>
                  <td className="px-4 py-2 text-12 text-ink-3">{description}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function SourceCodeSection({ code }: { code: string }): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Source code" subtitle={`${code.split('\n').length} lines`} />
      <details className="rounded-xl border bg-card text-card-foreground shadow-sm">
        <summary className="cursor-pointer select-none px-4 py-2 text-sm text-ink-2">
          Show source
        </summary>
        <pre className="m-0 overflow-x-auto rounded-b-xl bg-bg-sunk p-4 font-mono text-12 text-ink">
          {code}
        </pre>
      </details>
    </section>
  )
}

function ProgramSourceSection({
  routines
}: {
  routines: PlantPlcProgramDetail['routines']
}): React.ReactElement {
  if (routines.length === 1 && routines[0]?.source_code) {
    return <SourceCodeSection code={routines[0].source_code} />
  }

  return (
    <section>
      <SectionHeader
        label="Source code"
        subtitle={`${routines.length} ${routines.length === 1 ? 'routine' : 'routines'}`}
      />
      <div className="flex flex-col gap-3">
        {routines.map((routine) => (
          <details
            key={routine.id}
            className="rounded-xl border bg-card text-card-foreground shadow-sm"
          >
            <summary className="cursor-pointer select-none px-4 py-2 text-sm text-ink-2">
              {routine.name}
              {routine.type ? ` · ${routine.type}` : ''}
            </summary>
            <pre className="m-0 overflow-x-auto rounded-b-xl bg-bg-sunk p-4 font-mono text-12 text-ink">
              {routine.source_code}
            </pre>
          </details>
        ))}
      </div>
    </section>
  )
}

function NodeTileGrid({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))]">
      {children}
    </div>
  )
}

function NodeTile({
  entityId,
  href,
  kind,
  title,
  subtitle
}: {
  entityId: string
  href: string
  kind: PlantPlcNodeKind
  title: string
  subtitle?: string
}): React.ReactElement {
  const Icon = plcIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <Link
      to={href}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {title}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {subtitle && <span className="block truncate text-12 text-ink-3">{subtitle}</span>}
      </span>
    </Link>
  )
}

function PlcGhostTile({
  kind,
  ghost
}: {
  kind: PlantPlcNodeKind
  ghost: PlantSessionGhostView
}): React.ReactElement {
  const Icon = plcIconFor(kind)
  return (
    <SessionGhostTile
      name={ghost.name ?? ghost.id}
      view={ghost.view}
      icon={
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
          <Icon size={16} />
        </span>
      }
    />
  )
}

function ControlPatternChip({ pattern }: { pattern: PlantControlPattern }): React.ReactElement {
  const label = pattern.pattern_type ? `${pattern.name} · ${pattern.pattern_type}` : pattern.name
  return (
    <Badge variant="outline" title={pattern.description ?? undefined}>
      {label}
    </Badge>
  )
}

function Stat({
  label,
  value,
  hint,
  href
}: {
  label: string
  value: number | string
  hint?: string
  href?: string
}): React.ReactElement {
  const display = typeof value === 'number' ? value.toLocaleString() : value
  return (
    <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
      <div className="truncate text-2xl font-semibold tabular-nums text-ink" title={String(value)}>
        {href ? (
          <Link to={href} className="text-brand no-underline hover:underline">
            {display}
          </Link>
        ) : (
          display
        )}
      </div>
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
      {hint && <div className="mt-0.5 text-10 normal-case text-ink-4">{hint}</div>}
    </div>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}
